import type {
  CalendarEventCreateRequest,
  CalendarEventPatchOperation,
  CalendarEventResponse,
  CalendarRecurrence,
} from "calendar/_schemas/calendar-event-schema";
import {
  calendarEventDefaultTimes,
  calendarEventFieldLimits,
} from "calendar/calendar-event-fields";
import { localDateInputLength, toLocalDateInput } from "_shared/dates/date-input";

export type CalendarRecurrenceTermination = "never" | "count" | "until";

export type CalendarEventFormState = {
  title: string;
  isAllDay: boolean;
  startDate: string;
  endDate: string;
  startDateTime: string;
  endDateTime: string;
  timeZoneId: string;
  description: string;
  link: string;
  repeats: boolean;
  frequency: CalendarRecurrence["frequency"];
  interval: string;
  weekdays: CalendarRecurrence["weekdays"];
  termination: CalendarRecurrenceTermination;
  count: string;
  untilDate: string;
  untilDateTime: string;
};

export type CalendarEventFormErrors = Partial<Record<keyof CalendarEventFormState, string>>;

export function initialCalendarEventForm(defaultDate: string, event?: CalendarEventResponse): CalendarEventFormState {
  if (!event) {
    return {
      title: "",
      isAllDay: true,
      startDate: defaultDate,
      endDate: defaultDate,
      startDateTime: `${defaultDate}T${calendarEventDefaultTimes.start}`,
      endDateTime: `${defaultDate}T${calendarEventDefaultTimes.end}`,
      timeZoneId: browserTimeZone(),
      description: "",
      link: "",
      repeats: false,
      frequency: "weekly",
      interval: "1",
      weekdays: [],
      termination: "never",
      count: "",
      untilDate: defaultDate,
      untilDateTime: `${defaultDate}T${calendarEventDefaultTimes.start}`,
    };
  }

  const startDate = event.isAllDay
    ? String(event.start).slice(0, localDateInputLength)
    : toLocalDateInput(new Date(String(event.start)));
  const recurrence = event.recurrence;
  return {
    title: event.title,
    isAllDay: event.isAllDay,
    startDate,
    endDate: event.isAllDay && event.end !== null
      ? addDateDays(String(event.end).slice(0, localDateInputLength), -1)
      : startDate,
    startDateTime: event.isAllDay
      ? `${startDate}T${calendarEventDefaultTimes.start}`
      : toDateTimeLocalInput(new Date(String(event.start))),
    endDateTime: event.isAllDay
      ? `${startDate}T${calendarEventDefaultTimes.end}`
      : event.end === null ? "" : toDateTimeLocalInput(new Date(String(event.end))),
    timeZoneId: event.timeZoneId ?? browserTimeZone(),
    description: event.description ?? "",
    link: event.link ?? "",
    repeats: recurrence !== null,
    frequency: recurrence?.frequency ?? "weekly",
    interval: String(recurrence?.interval ?? 1),
    weekdays: recurrence?.weekdays ?? [],
    termination: recurrence?.count
      ? "count"
      : recurrence?.untilDate || recurrence?.untilUtc ? "until" : "never",
    count: recurrence?.count ? String(recurrence.count) : "",
    untilDate: recurrence?.untilDate ?? startDate,
    untilDateTime: recurrence?.untilUtc
      ? toDateTimeLocalInput(new Date(recurrence.untilUtc))
      : event.isAllDay
        ? `${startDate}T${calendarEventDefaultTimes.start}`
        : toDateTimeLocalInput(new Date(String(event.start))),
  };
}

export function validateCalendarEventForm(form: CalendarEventFormState): CalendarEventFormErrors {
  const errors: CalendarEventFormErrors = {};
  const title = form.title.trim();
  if (!title) errors.title = "Title is required.";
  else if (title.length > calendarEventFieldLimits.title) {
    errors.title = `Title must be ${calendarEventFieldLimits.title} characters or fewer.`;
  }
  if (form.description.length > calendarEventFieldLimits.description) {
    errors.description = `Description must be ${calendarEventFieldLimits.description.toLocaleString()} characters or fewer.`;
  }
  if (form.link.length > calendarEventFieldLimits.link) {
    errors.link = `Link must be ${calendarEventFieldLimits.link.toLocaleString()} characters or fewer.`;
  }

  if (form.isAllDay) {
    if (!form.startDate) errors.startDate = "Start date is required.";
    else if (!isValidDateInput(form.startDate)) errors.startDate = "Enter a valid start date.";
    if (!form.endDate) errors.endDate = "End date is required.";
    else if (!isValidDateInput(form.endDate)) errors.endDate = "Enter a valid end date.";
    if (!errors.startDate && !errors.endDate && form.endDate < form.startDate) {
      errors.endDate = "End date cannot be before the start date.";
    }
  } else {
    if (!form.startDateTime) errors.startDateTime = "Start date and time are required.";
    else if (!isValidDateTimeInput(form.startDateTime)) {
      errors.startDateTime = "Enter a valid start date and time.";
    }
    if (form.endDateTime && !isValidDateTimeInput(form.endDateTime)) {
      errors.endDateTime = "Enter a valid end date and time.";
    } else if (!errors.startDateTime && form.endDateTime
      && new Date(form.endDateTime).getTime() <= new Date(form.startDateTime).getTime()) {
      errors.endDateTime = "The end time must be after the start time. Change one of the times and try again.";
    }
    if (!form.timeZoneId.trim()) errors.timeZoneId = "Time zone is required.";
  }

  if (form.repeats) {
    const interval = Number(form.interval);
    if (!Number.isInteger(interval) || interval <= 0) errors.interval = "Interval must be a positive whole number.";
    if (form.frequency === "weekly" && form.weekdays.length === 0) {
      errors.weekdays = "Select at least one weekday.";
    }
    if (form.termination === "count") {
      const count = Number(form.count);
      if (!Number.isInteger(count) || count <= 0) errors.count = "Occurrence count must be a positive whole number.";
    }
    if (form.termination === "until") {
      if (form.isAllDay) {
        if (!form.untilDate) errors.untilDate = "An end date is required.";
        else if (!isValidDateInput(form.untilDate)) errors.untilDate = "Enter a valid recurrence end date.";
        else if (form.untilDate < form.startDate) errors.untilDate = "Recurrence cannot end before the event starts.";
      } else {
        if (!form.untilDateTime) errors.untilDateTime = "An end date and time are required.";
        else if (!isValidDateTimeInput(form.untilDateTime)) {
          errors.untilDateTime = "Enter a valid recurrence end date and time.";
        }
        else if (new Date(form.untilDateTime).getTime() < new Date(form.startDateTime).getTime()) {
          errors.untilDateTime = "Recurrence cannot end before the event starts.";
        }
      }
    }
  }
  return errors;
}

export function isCalendarEventFormValid(form: CalendarEventFormState): boolean {
  return Object.keys(validateCalendarEventForm(form)).length === 0;
}

export function isCalendarEventStartOnPastDay(
  form: CalendarEventFormState,
  referenceDate: Date = new Date(),
): boolean {
  const startDate = form.isAllDay
    ? form.startDate
    : form.startDateTime.slice(0, localDateInputLength);
  return isValidDateInput(startDate) && startDate < toLocalDateInput(referenceDate);
}

export function calendarEventCreateRequest(form: CalendarEventFormState): CalendarEventCreateRequest {
  const recurrence = calendarEventRecurrenceRequest(form);
  const common = {
    title: form.title.trim(),
    description: optionalText(form.description),
    link: optionalText(form.link),
    recurrence,
  };
  if (form.isAllDay) {
    return {
      ...common,
      startDate: form.startDate,
      endDate: addDateDays(form.endDate, 1),
    };
  }
  return {
    ...common,
    start: new Date(form.startDateTime).toISOString(),
    end: form.endDateTime ? new Date(form.endDateTime).toISOString() : undefined,
    timeZoneId: form.timeZoneId,
  };
}

export function calendarEventPatch(form: CalendarEventFormState): CalendarEventPatchOperation[] {
  const recurrence = calendarEventRecurrenceRequest(form);
  const values: Record<string, unknown> = {
    title: form.title.trim(),
    startDate: form.isAllDay ? form.startDate : null,
    endDate: form.isAllDay ? addDateDays(form.endDate, 1) : null,
    start: form.isAllDay ? null : new Date(form.startDateTime).toISOString(),
    end: form.isAllDay || !form.endDateTime ? null : new Date(form.endDateTime).toISOString(),
    timeZoneId: form.isAllDay ? null : form.timeZoneId,
    description: optionalText(form.description) ?? null,
    link: optionalText(form.link) ?? null,
    recurrence: recurrence ?? null,
    clearRecurrence: recurrence === undefined,
  };
  return Object.entries(values).map(([path, value]) => ({ op: "replace", path: `/${path}`, value }));
}

export function toggleCalendarEventTemporalMode(form: CalendarEventFormState, isAllDay: boolean): CalendarEventFormState {
  if (isAllDay) {
    const date = form.startDate
      || form.startDateTime.slice(0, localDateInputLength)
      || toLocalDateInput(new Date());
    return { ...form, isAllDay: true, startDate: date, endDate: form.endDate || date };
  }
  const date = form.startDate || toLocalDateInput(new Date());
  return {
    ...form,
    isAllDay: false,
    startDateTime: form.startDateTime || `${date}T${calendarEventDefaultTimes.start}`,
    endDateTime: form.endDateTime || `${date}T${calendarEventDefaultTimes.end}`,
  };
}

function calendarEventRecurrenceRequest(form: CalendarEventFormState): CalendarEventCreateRequest["recurrence"] {
  if (!form.repeats) return undefined;
  return {
    frequency: form.frequency,
    interval: Number(form.interval),
    weekdays: form.frequency === "weekly" ? form.weekdays : [],
    count: form.termination === "count" ? Number(form.count) : undefined,
    untilDate: form.termination === "until" && form.isAllDay ? form.untilDate : undefined,
    until: form.termination === "until" && !form.isAllDay
      ? new Date(form.untilDateTime).toISOString()
      : undefined,
  };
}

function optionalText(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed || undefined;
}

function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

function addDateDays(value: string, days: number): string {
  const date = new Date(`${value}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, localDateInputLength);
}

function toDateTimeLocalInput(value: Date): string {
  const date = toLocalDateInput(value);
  const hours = String(value.getHours()).padStart(2, "0");
  const minutes = String(value.getMinutes()).padStart(2, "0");
  return `${date}T${hours}:${minutes}`;
}

function isValidDateInput(value: string): boolean {
  const match = /^(?<year>\d{4})-(?<month>\d{2})-(?<day>\d{2})$/.exec(value);
  if (!match?.groups) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime())
    && date.getUTCFullYear() === Number(match.groups.year)
    && date.getUTCMonth() + 1 === Number(match.groups.month)
    && date.getUTCDate() === Number(match.groups.day);
}

function isValidDateTimeInput(value: string): boolean {
  const match = /^(?<date>\d{4}-\d{2}-\d{2})T(?<hour>\d{2}):(?<minute>\d{2})$/.exec(value);
  if (!match?.groups || !isValidDateInput(match.groups.date)) return false;
  const parsed = new Date(value);
  return !Number.isNaN(parsed.getTime())
    && parsed.getHours() === Number(match.groups.hour)
    && parsed.getMinutes() === Number(match.groups.minute);
}
